import * as contentData from '../content/contentData';

type ContentType = 'about' | 'services' | 'cases' | 'tech' | 'team' | 'contact' | 'news' | 'careers';

export class ContentOverlay {
  private overlay: HTMLElement;
  private content: HTMLElement;
  private isOpen = false;
  private onCloseCallback?: () => void;

  constructor() {
    this.overlay = this.createOverlay();
    this.content = this.overlay.querySelector('.overlay-content') as HTMLElement;
    document.body.appendChild(this.overlay);
    this.addStyles();
  }

  private createOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.id = 'content-overlay';
    overlay.innerHTML = `
      <div class="overlay-backdrop"></div>
      <div class="overlay-container">
        <button class="overlay-close" type="button">
          <span class="close-x">✕</span>
          <span class="close-text">ESC</span>
        </button>
        <div class="overlay-scroll">
          <div class="overlay-content"></div>
        </div>
      </div>
    `;

    // Close on backdrop click
    const backdrop = overlay.querySelector('.overlay-backdrop') as HTMLElement;
    backdrop.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    // Close on button click - using a direct reference
    const closeBtn = overlay.querySelector('.overlay-close') as HTMLButtonElement;
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    });

    // ESC key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    return overlay;
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.id = 'overlay-styles';
    style.textContent = `
      #content-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 3000;
        display: none;
        opacity: 0;
        transition: opacity 0.4s ease;
      }
      
      #content-overlay.open {
        display: flex;
        opacity: 1;
      }
      
      .overlay-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.85);
        backdrop-filter: blur(20px);
      }
      
      .overlay-container {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        padding: 60px;
        overflow: hidden;
      }
      
      .overlay-close {
        position: absolute;
        top: 30px;
        right: 40px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: white;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        font-size: 20px;
        transition: all 0.3s ease;
        z-index: 10;
      }
      
      .overlay-close:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(0, 212, 255, 0.5);
        transform: scale(1.1);
      }
      
      .overlay-close .close-text {
        font-size: 8px;
        letter-spacing: 1px;
        opacity: 0.5;
      }
      
      .overlay-scroll {
        flex: 1;
        overflow-y: auto;
        padding-right: 20px;
      }
      
      .overlay-scroll::-webkit-scrollbar {
        width: 4px;
      }
      
      .overlay-scroll::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
      }
      
      .overlay-scroll::-webkit-scrollbar-thumb {
        background: rgba(0, 212, 255, 0.3);
        border-radius: 2px;
      }
      
      .overlay-content {
        max-width: 1000px;
        margin: 0 auto;
        animation: slideUp 0.5s ease;
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      /* Content Typography */
      .content-header {
        margin-bottom: 50px;
        text-align: center;
      }
      
      .content-title {
        font-family: 'Orbitron', sans-serif;
        font-size: 42px;
        font-weight: 700;
        letter-spacing: 4px;
        color: #ffffff;
        margin-bottom: 15px;
        text-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
      }
      
      .content-subtitle {
        font-family: 'Inter', sans-serif;
        font-size: 16px;
        color: #00d4ff;
        letter-spacing: 2px;
        font-weight: 300;
      }
      
      .content-description {
        font-size: 16px;
        line-height: 1.8;
        color: #a0a0b0;
        margin: 30px 0;
        max-width: 800px;
        margin-left: auto;
        margin-right: auto;
      }
      
      .content-section {
        margin: 50px 0;
      }
      
      .section-title {
        font-family: 'Orbitron', sans-serif;
        font-size: 18px;
        letter-spacing: 3px;
        color: #00d4ff;
        margin-bottom: 25px;
        padding-bottom: 10px;
        border-bottom: 1px solid rgba(0, 212, 255, 0.2);
      }
      
      /* Cards Grid */
      .cards-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 25px;
      }
      
      .card {
        background: linear-gradient(135deg, rgba(20, 20, 40, 0.8) 0%, rgba(10, 10, 25, 0.9) 100%);
        border: 1px solid rgba(0, 212, 255, 0.15);
        border-radius: 12px;
        padding: 30px;
        transition: all 0.3s ease;
      }
      
      .card:hover {
        border-color: rgba(0, 212, 255, 0.4);
        transform: translateY(-5px);
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
      }
      
      .card-icon {
        font-size: 36px;
        margin-bottom: 15px;
      }
      
      .card-title {
        font-family: 'Orbitron', sans-serif;
        font-size: 16px;
        letter-spacing: 1px;
        color: #ffffff;
        margin-bottom: 10px;
      }
      
      .card-text {
        font-size: 14px;
        line-height: 1.6;
        color: #8080a0;
      }
      
      .card-meta {
        font-size: 12px;
        color: #606080;
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid rgba(255, 255, 255, 0.05);
      }
      
      .card-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 15px;
      }
      
      .tag {
        font-size: 10px;
        padding: 4px 10px;
        background: rgba(0, 212, 255, 0.1);
        border: 1px solid rgba(0, 212, 255, 0.2);
        border-radius: 20px;
        color: #00d4ff;
        letter-spacing: 0.5px;
      }
      
      /* Stats */
      .stats-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 20px;
        margin: 40px 0;
      }
      
      .stat-item {
        text-align: center;
        padding: 25px;
        background: rgba(0, 212, 255, 0.05);
        border-radius: 10px;
        border: 1px solid rgba(0, 212, 255, 0.1);
      }
      
      .stat-value {
        font-family: 'Orbitron', sans-serif;
        font-size: 32px;
        font-weight: 700;
        color: #00d4ff;
        margin-bottom: 8px;
      }
      
      .stat-label {
        font-size: 12px;
        color: #6b7280;
        letter-spacing: 1px;
        text-transform: uppercase;
      }
      
      /* Article Content */
      .article-content {
        background: rgba(15, 15, 30, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.05);
        border-radius: 12px;
        padding: 40px;
        margin: 30px 0;
      }
      
      .article-content h2 {
        font-family: 'Orbitron', sans-serif;
        font-size: 24px;
        color: #ffffff;
        margin: 30px 0 15px 0;
        letter-spacing: 1px;
      }
      
      .article-content h3 {
        font-size: 20px;
        color: #00d4ff;
        margin: 25px 0 12px 0;
      }
      
      .article-content h4 {
        font-size: 16px;
        color: #a0a0c0;
        margin: 20px 0 10px 0;
      }
      
      .article-content p {
        font-size: 15px;
        line-height: 1.8;
        color: #b0b0c0;
        margin: 15px 0;
      }
      
      .article-content ul {
        margin: 15px 0;
        padding-left: 25px;
      }
      
      .article-content li {
        font-size: 14px;
        line-height: 1.8;
        color: #a0a0b0;
        margin: 8px 0;
      }
      
      .article-content blockquote {
        border-left: 3px solid #00d4ff;
        padding: 15px 25px;
        margin: 25px 0;
        background: rgba(0, 212, 255, 0.05);
        font-style: italic;
        color: #c0c0d0;
      }
      
      /* List Items */
      .list-item {
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 20px;
        background: rgba(15, 15, 30, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.05);
        border-radius: 8px;
        margin: 10px 0;
        transition: all 0.3s ease;
      }
      
      .list-item:hover {
        border-color: rgba(0, 212, 255, 0.3);
        background: rgba(20, 20, 40, 0.6);
      }
      
      .list-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 212, 255, 0.1);
        border-radius: 8px;
        color: #00d4ff;
      }
      
      .list-content {
        flex: 1;
      }
      
      .list-title {
        font-size: 15px;
        color: #ffffff;
        margin-bottom: 4px;
      }
      
      .list-subtitle {
        font-size: 12px;
        color: #6b7280;
      }
      
      /* Contact Form */
      .contact-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 40px;
        margin: 40px 0;
      }
      
      .contact-info-item {
        display: flex;
        align-items: center;
        gap: 15px;
        margin: 20px 0;
      }
      
      .contact-icon {
        width: 50px;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 212, 255, 0.1);
        border-radius: 12px;
        font-size: 20px;
      }
      
      .contact-label {
        font-size: 12px;
        color: #6b7280;
        margin-bottom: 4px;
      }
      
      .contact-value {
        font-size: 16px;
        color: #ffffff;
      }
      
      /* Benefits */
      .benefits-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 15px;
        margin: 30px 0;
      }
      
      .benefit-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 15px;
        background: rgba(0, 212, 255, 0.05);
        border-radius: 8px;
        font-size: 13px;
        color: #a0a0c0;
      }
      
      .benefit-icon {
        color: #00ff88;
        font-size: 16px;
      }
      
      /* Technology Pills */
      .tech-category {
        margin: 25px 0;
      }
      
      .tech-category-title {
        font-size: 14px;
        color: #00d4ff;
        margin-bottom: 12px;
        letter-spacing: 1px;
      }
      
      .tech-pills {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
      }
      
      .tech-pill {
        padding: 8px 16px;
        background: rgba(124, 58, 237, 0.1);
        border: 1px solid rgba(124, 58, 237, 0.3);
        border-radius: 20px;
        font-size: 12px;
        color: #c0a0ff;
      }
      
      /* Read More Button */
      .read-more-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        background: transparent;
        border: 1px solid rgba(0, 212, 255, 0.4);
        color: #00d4ff;
        font-family: 'Orbitron', sans-serif;
        font-size: 11px;
        letter-spacing: 2px;
        cursor: pointer;
        transition: all 0.3s ease;
        margin-top: 15px;
      }
      
      .read-more-btn:hover {
        background: rgba(0, 212, 255, 0.1);
        border-color: #00d4ff;
      }
      
      /* Responsive */
      @media (max-width: 768px) {
        .overlay-container {
          padding: 30px 20px;
        }
        
        .overlay-close {
          top: 15px;
          right: 15px;
          width: 50px;
          height: 50px;
        }
        
        .content-title {
          font-size: 28px;
        }
        
        .stats-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        
        .contact-grid {
          grid-template-columns: 1fr;
        }
        
        .benefits-grid {
          grid-template-columns: 1fr;
        }
      }
    `;
    document.head.appendChild(style);
  }

  public open(screenId: ContentType, onClose?: () => void): void {
    this.onCloseCallback = onClose;
    this.content.innerHTML = this.getContentForScreen(screenId);
    this.overlay.classList.add('open');
    this.isOpen = true;
    document.body.style.overflow = 'hidden';
  }

  public close(): void {
    this.overlay.classList.remove('open');
    this.isOpen = false;
    document.body.style.overflow = '';
    if (this.onCloseCallback) {
      this.onCloseCallback();
    }
  }

  private getContentForScreen(screenId: ContentType): string {
    switch (screenId) {
      case 'about':
        return this.renderAbout();
      case 'services':
        return this.renderServices();
      case 'cases':
        return this.renderCaseStudies();
      case 'tech':
        return this.renderTechnology();
      case 'team':
        return this.renderTeam();
      case 'contact':
        return this.renderContact();
      case 'news':
        return this.renderNews();
      case 'careers':
        return this.renderCareers();
      default:
        return '<p>Content not found</p>';
    }
  }

  private renderAbout(): string {
    const about = contentData.getAboutContent();
    return `
      <div class="content-header">
        <h1 class="content-title">${about.title}</h1>
        <p class="content-subtitle">${about.tagline}</p>
        <p class="content-subtitle" style="margin-top: 10px; font-size: 20px; font-weight: 600;">${about.subtitle}</p>
      </div>
      
      <!-- Who We Are -->
      <div class="content-section">
        <h2 class="section-title">${about.whoWeAre.title}</h2>
        ${about.whoWeAre.paragraphs.map((p: any) => `<p class="content-description">${p}</p>`).join('')}
      </div>
      
      <!-- Philosophy -->
      <div class="content-section">
        <h2 class="section-title">${about.philosophy.title}</h2>
        <p class="content-description" style="font-style: italic; text-align: center;">${about.philosophy.intro}</p>
        <p class="content-description" style="text-align: center;">${about.philosophy.belief}</p>
        <div class="cards-grid" style="margin-top: 30px;">
          ${about.philosophy.pillars.map((pillar: any, idx: number) => `
            <div class="card">
              <div class="card-icon" style="color: ${['#00d4ff', '#7b2fff', '#00ff88'][idx]};">${['🏛️', '⚡', '🤖'][idx]}</div>
              <div class="card-title">${pillar.name}</div>
              <div class="card-text">${pillar.description}</div>
            </div>
          `).join('')}
        </div>
      </div>
      
      <!-- Solutions Ecosystem -->
      <div class="content-section">
        <h2 class="section-title">${about.solutions.title}</h2>
        <p class="content-description">${about.solutions.intro}</p>
        
        ${about.solutions.engines.map((engine: any, idx: number) => `
          <div style="margin: 40px 0;">
            <h3 style="font-family: 'Orbitron', sans-serif; font-size: 16px; color: ${idx === 0 ? '#7b2fff' : '#00ff88'}; margin-bottom: 20px; letter-spacing: 2px;">
              ${idx + 1}. ${engine.name}
            </h3>
            <div class="cards-grid">
              ${engine.products.map((product: any) => `
                <div class="list-item">
                  <div class="list-icon">${idx === 0 ? '⚙️' : '📈'}</div>
                  <div class="list-content">
                    <div class="list-title">${product.name}</div>
                    <div class="list-subtitle" style="color: #a0a0b0;">${product.description}</div>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        `).join('')}
      </div>
      
      <!-- A.C.C.U.R.A.T.E. Standard -->
      <div class="content-section">
        <h2 class="section-title">${about.standard.title}</h2>
        <p class="content-description">${about.standard.intro}</p>
        <div class="cards-grid" style="grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); margin-top: 30px;">
          ${about.standard.items.map((item: any) => `
            <div class="card" style="text-align: center; padding: 20px;">
              <div style="font-family: 'Orbitron', sans-serif; font-size: 28px; color: #00d4ff; margin-bottom: 10px;">${item.letter}</div>
              <div class="card-title" style="color: #00ff88;">${item.term}</div>
              <div class="card-text" style="font-size: 12px;">${item.description}</div>
            </div>
          `).join('')}
        </div>
      </div>
      
      <!-- Mission -->
      <div class="content-section">
        <h2 class="section-title">OUR MISSION</h2>
        <div class="article-content" style="text-align: center;">
          <blockquote style="border-left: none; border: 2px solid rgba(0, 212, 255, 0.3); border-radius: 12px; padding: 30px;">
            ${about.mission}
          </blockquote>
        </div>
      </div>
      
      <!-- Mercury by the Numbers -->
      <div class="content-section">
        <h2 class="section-title">MERCURY BY THE NUMBERS</h2>
        <div class="stats-grid" style="grid-template-columns: repeat(3, 1fr);">
          ${about.stats.map((stat: any) => `
            <div class="stat-item">
              <div class="stat-value">${stat.value}</div>
              <div class="stat-label">${stat.label}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderServices(): string {
    const services = contentData.getServicesContent();
    return `
      <div class="content-header">
        <h1 class="content-title">${services.title}</h1>
        <p class="content-subtitle">${services.subtitle}</p>
        <p class="content-subtitle" style="margin-top: 10px; font-size: 20px; font-weight: 600;">${services.tagline}</p>
      </div>
      
      <!-- Intro -->
      <div class="content-section">
        <p class="content-description">${services.intro}</p>
      </div>
      
      <!-- Service Categories -->
      ${services.categories.map((category: any) => `
        <div class="content-section">
          <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 20px;">
            <span style="font-size: 36px;">${category.icon}</span>
            <h2 class="section-title" style="border-bottom: none; padding-bottom: 0; margin-bottom: 0;">${category.title}</h2>
          </div>
          <p class="content-description" style="margin-bottom: 25px;">${category.description}</p>
          
          <div class="cards-grid">
            ${category.services.map((service: any) => `
              <div class="card">
                <div class="card-title" style="color: #00d4ff;">${service.name}</div>
                <div class="card-text">${service.description}</div>
              </div>
            `).join('')}
          </div>
        </div>
      `).join('')}
    `;
  }

  private renderCaseStudies(): string {
    return `
      <div class="content-header">
        <h1 class="content-title">Case Studies</h1>
        <p class="content-subtitle">Real Results for Real Clients</p>
      </div>
      
      ${contentData.caseStudies.map((study: any) => `
        <div class="card" style="margin: 30px 0;">
          <div class="card-title" style="font-size: 20px; margin-bottom: 20px;">${study.title}</div>
          <div class="card-meta" style="border-top: none; padding-top: 0; margin-top: 0;">
            <strong>Client:</strong> ${study.client} | <strong>Industry:</strong> ${study.industry}
          </div>
          
          <div style="margin: 25px 0;">
            <h4 style="color: #ff6b35; margin-bottom: 10px;">The Challenge</h4>
            <p class="card-text">${study.challenge}</p>
          </div>
          
          <div style="margin: 25px 0;">
            <h4 style="color: #00ff88; margin-bottom: 10px;">Our Solution</h4>
            <p class="card-text">${study.solution}</p>
          </div>
          
          <div style="margin: 25px 0;">
            <h4 style="color: #00d4ff; margin-bottom: 10px;">Results</h4>
            <ul style="list-style: none; padding: 0;">
              ${study.results.map((r: any) => `<li style="padding: 8px 0; color: #a0a0c0;">✓ ${r}</li>`).join('')}
            </ul>
          </div>
          
          <div class="card-tags">
            ${study.technologies.map((t: any) => `<span class="tag">${t}</span>`).join('')}
          </div>
        </div>
      `).join('')}
    `;
  }

  private renderTechnology(): string {
    const tech = contentData.technologyContent;
    return `
      <div class="content-header">
        <h1 class="content-title">${tech.title}</h1>
        <p class="content-subtitle">${tech.subtitle}</p>
        <p class="content-subtitle" style="margin-top: 10px; font-size: 20px; font-weight: 600;">${tech.tagline}</p>
      </div>
      
      <!-- Intro -->
      <div class="content-section">
        <p class="content-description">${tech.intro}</p>
      </div>
      
      <!-- Technology Categories -->
      ${tech.categories.map((category: any) => `
        <div class="content-section">
          <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 20px;">
            <span style="font-size: 36px;">${category.icon}</span>
            <h2 class="section-title" style="border-bottom: none; padding-bottom: 0; margin-bottom: 0;">${category.title}</h2>
          </div>
          <p class="content-description" style="margin-bottom: 25px; font-style: italic;">${category.description}</p>
          
          <div class="cards-grid">
            ${category.technologies.map((tech: any) => `
              <div class="card">
                <div class="card-title" style="color: #00ff88;">${tech.name}</div>
                <div class="card-text">${tech.description}</div>
              </div>
            `).join('')}
          </div>
        </div>
      `).join('')}
    `;
  }

  private renderTeam(): string {
    const team = contentData.getTeamContent();
    return `
      <div class="content-header">
        <h1 class="content-title">${team.title}</h1>
        <p class="content-subtitle">${team.subtitle}</p>
        <p class="content-subtitle" style="margin-top: 10px; font-size: 20px; font-weight: 600;">${team.tagline}</p>
      </div>
      
      <!-- Intro -->
      <div class="content-section">
        <p class="content-description">${team.intro}</p>
      </div>
      
      <!-- Team Members -->
      <div class="content-section">
        ${team.members.map((member: any) => `
          <div class="card" style="margin-bottom: 30px; display: flex; flex-direction: column; align-items: flex-start;">
            <div style="display: flex; align-items: center; gap: 20px; margin-bottom: 20px; width: 100%;">
              <div style="width: 80px; height: 80px; min-width: 80px; background: linear-gradient(135deg, #00d4ff, #7c3aed); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 32px; color: white; font-family: 'Orbitron', sans-serif;">
                ${member.name.charAt(0)}
              </div>
              <div>
                <div class="card-title" style="font-size: 20px; margin-bottom: 5px;">${member.name}</div>
                <div style="color: #00d4ff; font-size: 12px; letter-spacing: 1px; margin-bottom: 5px;">${member.role}</div>
                <div style="color: #00ff88; font-size: 14px; font-style: italic;">${member.title}</div>
              </div>
            </div>
            <div class="card-text" style="line-height: 1.7;">${member.bio}</div>
          </div>
        `).join('')}
      </div>
    `;
  }

  private renderContact(): string {
    const contact = contentData.contactInfo;
    return `
      <div class="content-header">
        <h1 class="content-title">${contact.title}</h1>
        <p class="content-subtitle">${contact.subtitle}</p>
        <p class="content-subtitle" style="margin-top: 10px; font-size: 20px; font-weight: 600;">${contact.tagline}</p>
      </div>
      
      <!-- Intro Section -->
      <div class="content-section">
        ${contact.intro.map((p: any) => `<p class="content-description">${p}</p>`).join('')}
      </div>
      
      <!-- Global Headquarters -->
      <div class="content-section">
        <h2 class="section-title">${contact.locationsTitle}</h2>
        <p class="content-description" style="text-align: center; font-style: italic; margin-bottom: 30px;">${contact.locationsSubtitle}</p>
        
        <div class="cards-grid">
          ${contact.locations.map((loc: any) => `
            <div class="card">
              <div class="card-icon" style="font-size: 36px;">📍</div>
              <div class="card-title" style="color: #00d4ff; font-size: 18px;">${loc.city}</div>
              <div style="color: #00ff88; font-size: 12px; letter-spacing: 1px; margin-bottom: 15px;">${loc.tagline}</div>
              ${loc.address ? `<div class="card-text" style="margin-bottom: 10px;"><strong>Address:</strong> ${loc.address}</div>` : ''}
              <div class="card-text"><strong>Focus:</strong> ${loc.focus}</div>
            </div>
          `).join('')}
        </div>
      </div>
      
      <!-- Contact Methods -->
      <div class="content-section">
        <h2 class="section-title">${contact.contactMethods.title}</h2>
        
        <div class="contact-grid">
          <div class="card" style="text-align: center;">
            <div class="card-icon" style="font-size: 48px;">📧</div>
            <div class="card-title">Email Us</div>
            <div style="font-size: 18px; color: #00d4ff; margin: 15px 0;">
              <a href="mailto:${contact.contactMethods.email.address}" style="color: #00d4ff; text-decoration: none;">${contact.contactMethods.email.address}</a>
            </div>
            <div class="card-text">${contact.contactMethods.email.note}</div>
          </div>
          
          <div class="card" style="text-align: center;">
            <div class="card-icon" style="font-size: 48px;">📱</div>
            <div class="card-title">Call Us</div>
            <div style="font-size: 18px; color: #00d4ff; margin: 15px 0;">
              <a href="tel:${contact.contactMethods.phone.number}" style="color: #00d4ff; text-decoration: none;">${contact.contactMethods.phone.number}</a>
            </div>
            <div class="card-text">${contact.contactMethods.phone.note}</div>
          </div>
        </div>
      </div>
      
      <!-- What Happens Next -->
      <div class="content-section">
        <h2 class="section-title">${contact.nextSteps.title}</h2>
        <div class="cards-grid" style="grid-template-columns: repeat(3, 1fr);">
          ${contact.nextSteps.steps.map((step: any, idx: number) => `
            <div class="card" style="text-align: center;">
              <div style="font-family: 'Orbitron', sans-serif; font-size: 48px; color: #00d4ff; margin-bottom: 15px;">${idx + 1}</div>
              <div class="card-title" style="color: #00ff88;">${step.name}</div>
              <div class="card-text">${step.description}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderNews(): string {
    return `
      <div class="content-header">
        <h1 class="content-title">News & Updates</h1>
        <p class="content-subtitle">Stay Informed</p>
      </div>
      
      <div class="content-section">
        <h2 class="section-title">LATEST NEWS</h2>
        ${contentData.newsArticles.map((article: any) => `
          <div class="card" style="margin: 20px 0;">
            <div class="card-meta" style="border: none; padding: 0; margin: 0 0 10px 0;">
              <span class="tag">${article.category}</span>
              <span style="margin-left: 10px;">${article.date}</span>
            </div>
            <div class="card-title" style="font-size: 18px;">${article.title}</div>
            <div class="card-text">${article.excerpt}</div>
            <div class="article-content" style="margin-top: 20px;">
              ${article.content}
            </div>
          </div>
        `).join('')}
      </div>
      
      <div class="content-section">
        <h2 class="section-title">FROM THE BLOG</h2>
        ${contentData.blogPosts.map((post: any) => `
          <div class="card" style="margin: 20px 0;">
            <div class="card-meta" style="border: none; padding: 0; margin: 0 0 10px 0;">
              ${post.readTime} • ${post.date}
            </div>
            <div class="card-title" style="font-size: 18px;">${post.title}</div>
            <div class="card-text">${post.excerpt}</div>
            <div class="card-tags">
              ${post.tags.map((t: any) => `<span class="tag">${t}</span>`).join('')}
            </div>
            <div class="article-content" style="margin-top: 20px;">
              ${post.content}
            </div>
            <div class="card-meta">By ${post.author}</div>
          </div>
        `).join('')}
      </div>
    `;
  }

  private renderCareers(): string {
    const careers = contentData.careersContent;
    return `
      <div class="content-header">
        <h1 class="content-title">${careers.title}</h1>
        <p class="content-subtitle">${careers.subtitle}</p>
        <p class="content-subtitle" style="margin-top: 10px; font-size: 20px; font-weight: 600;">${careers.tagline}</p>
      </div>
      
      <!-- Intro Section -->
      <div class="content-section">
        <h2 class="section-title">${careers.intro.title}</h2>
        ${careers.intro.paragraphs.map((p: any) => `<p class="content-description">${p}</p>`).join('')}
      </div>
      
      <!-- Why Join Section -->
      <div class="content-section">
        <h2 class="section-title">${careers.whyJoin.title}</h2>
        <p class="content-description" style="text-align: center; font-style: italic; margin-bottom: 30px;">${careers.whyJoin.intro}</p>
        <div class="cards-grid">
          ${careers.whyJoin.benefits.map((benefit: any) => `
            <div class="card">
              <div class="card-icon" style="font-size: 40px;">${benefit.icon}</div>
              <div class="card-title" style="color: #00ff88;">${benefit.title}</div>
              <div class="card-text">${benefit.description}</div>
            </div>
          `).join('')}
        </div>
      </div>
      
      <!-- Open Positions -->
      <div class="content-section">
        <h2 class="section-title">OPEN POSITIONS</h2>
        ${careers.openings.map((job: any) => `
          <div class="list-item">
            <div class="list-icon">💼</div>
            <div class="list-content">
              <div class="list-title">${job.title}</div>
              <div class="list-subtitle">${job.location} • ${job.type}</div>
            </div>
            <button class="read-more-btn">APPLY →</button>
          </div>
        `).join('')}
      </div>
    `;
  }
}
